import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const DEFAULT = {
  hero_title: "", hero_subtitle: "", hero_button_text: "", hero_image: "",
  featured_title: "", featured_subtitle: "",
  candle_title: "", candle_subtitle: "", candle_image: "",
  soap_title: "", soap_subtitle: "", soap_image: "",
  custom_title: "", custom_subtitle: "", custom_image: "",
  gift_title: "", gift_subtitle: "",
  story_title: "", story_text: "", story_image: "",
  cta_title: "", cta_subtitle: "",
  instagram_images: [], testimonials: [],
};

export default function AdminHomepage() {
  const [form, setForm] = useState(DEFAULT);
  const [instaText, setInstaText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/homepage").then((r) => {
      const d = r.data || {};
      setForm({ ...DEFAULT, ...d, testimonials: d.testimonials || [] });
      setInstaText((d.instagram_images || []).join("\n"));
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateTest = (i, k, v) => {
    const arr = [...(form.testimonials || [])];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, testimonials: arr });
  };
  const addTest = () => setForm({ ...form, testimonials: [...(form.testimonials || []), { name: "", city: "", text: "" }] });
  const removeTest = (i) => setForm({ ...form, testimonials: form.testimonials.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = {
        ...form,
        instagram_images: instaText.split("\n").map((s) => s.trim()).filter(Boolean),
        testimonials: form.testimonials || [],
      };
      await api.put("/admin/homepage", body);
      toast.success("Anasayfa içeriği güncellendi");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-homepage">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl">Anasayfa İçeriği</h1>
          <p className="text-larc-muted mt-1">Anasayfada görünen tüm metin ve görselleri yönetin.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold transition-colors disabled:opacity-50" data-testid="home-save">
          <Save size={16} /> {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>

      <form onSubmit={save} className="mt-8 space-y-8">
        <Section title="Hero (Üst Bölüm)">
          <Field label="Başlık" value={form.hero_title} onChange={(v) => set("hero_title", v)} testid="hp-hero-title" />
          <Field label="Alt Başlık" textarea value={form.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} />
          <Field label="Buton Yazısı" value={form.hero_button_text} onChange={(v) => set("hero_button_text", v)} />
          <Field label="Görsel URL" value={form.hero_image} onChange={(v) => set("hero_image", v)} />
        </Section>

        <Section title="Öne Çıkan Ürünler">
          <Field label="Başlık" value={form.featured_title} onChange={(v) => set("featured_title", v)} />
          <Field label="Alt Başlık" value={form.featured_subtitle} onChange={(v) => set("featured_subtitle", v)} />
        </Section>

        <Section title="Mum Kategorisi">
          <Field label="Başlık" value={form.candle_title} onChange={(v) => set("candle_title", v)} />
          <Field label="Alt Başlık" value={form.candle_subtitle} onChange={(v) => set("candle_subtitle", v)} />
          <Field label="Görsel URL" value={form.candle_image} onChange={(v) => set("candle_image", v)} />
        </Section>

        <Section title="Sabun Kategorisi">
          <Field label="Başlık" value={form.soap_title} onChange={(v) => set("soap_title", v)} />
          <Field label="Alt Başlık" value={form.soap_subtitle} onChange={(v) => set("soap_subtitle", v)} />
          <Field label="Görsel URL" value={form.soap_image} onChange={(v) => set("soap_image", v)} />
        </Section>

        <Section title="Kişiye Özel Üretim">
          <Field label="Başlık" value={form.custom_title} onChange={(v) => set("custom_title", v)} />
          <Field label="Alt Başlık" textarea value={form.custom_subtitle} onChange={(v) => set("custom_subtitle", v)} />
          <Field label="Görsel URL" value={form.custom_image} onChange={(v) => set("custom_image", v)} />
        </Section>

        <Section title="Hediye Setleri">
          <Field label="Başlık" value={form.gift_title} onChange={(v) => set("gift_title", v)} />
          <Field label="Alt Başlık" value={form.gift_subtitle} onChange={(v) => set("gift_subtitle", v)} />
        </Section>

        <Section title="Marka Hikâyesi">
          <Field label="Başlık" value={form.story_title} onChange={(v) => set("story_title", v)} />
          <Field label="Metin" textarea rows={4} value={form.story_text} onChange={(v) => set("story_text", v)} />
          <Field label="Görsel URL" value={form.story_image} onChange={(v) => set("story_image", v)} />
        </Section>

        <Section title="CTA / Bülten">
          <Field label="Başlık" value={form.cta_title} onChange={(v) => set("cta_title", v)} />
          <Field label="Alt Başlık" textarea value={form.cta_subtitle} onChange={(v) => set("cta_subtitle", v)} />
        </Section>

        <Section title="Instagram Görselleri (her satıra bir URL)">
          <textarea rows={6} value={instaText} onChange={(e) => setInstaText(e.target.value)} className="w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" />
        </Section>

        <Section title="Yorumlar">
          <div className="space-y-4">
            {(form.testimonials || []).map((t, i) => (
              <div key={i} className="grid sm:grid-cols-2 gap-3 bg-larc-alt rounded-xl p-4">
                <input value={t.name} onChange={(e) => updateTest(i, "name", e.target.value)} placeholder="Ad" className="border border-larc-border rounded-lg px-3 py-2 text-sm bg-white" />
                <input value={t.city} onChange={(e) => updateTest(i, "city", e.target.value)} placeholder="Şehir" className="border border-larc-border rounded-lg px-3 py-2 text-sm bg-white" />
                <textarea rows={2} value={t.text} onChange={(e) => updateTest(i, "text", e.target.value)} placeholder="Yorum" className="sm:col-span-2 border border-larc-border rounded-lg px-3 py-2 text-sm bg-white" />
                <button type="button" onClick={() => removeTest(i)} className="text-xs text-red-600 sm:col-span-2 text-left">Sil</button>
              </div>
            ))}
            <button type="button" onClick={addTest} className="text-sm text-larc-gold hover:text-larc-gold-hover">+ Yorum Ekle</button>
          </div>
        </Section>

        <div className="pt-4 flex justify-end">
          <button disabled={saving} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? "Kaydediliyor…" : "Tümünü Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-larc-border rounded-2xl p-6">
      <h3 className="font-heading text-2xl mb-5">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, rows = 2, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}</span>
      {textarea ? (
        <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" data-testid={testid} />
      ) : (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" data-testid={testid} />
      )}
    </label>
  );
}
